import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'app/rootReducer'
import {
  Formik,
  Field,
  Form,
  FormikHelpers,
  ErrorMessage,
  FormikProps
} from 'formik'
import * as Yup from 'yup'
import Button from 'react-bootstrap/Button'
import {
  UpdateAccountMobilePhoneVariables,
  UpdateAccountMobilePhoneResponseData
} from 'share/mutations/account'
import { UserProfile } from 'reducers/userprofile/userprofileSlice'
import { Authentication } from 'share/domain/authentication'
import { phoneRegExp } from 'share/utils/utils'
import './styles.scss'
import { ButtonsContainer } from 'components/features/dashboard/questionnaire/QuestionnareForm/styles'
import { TwoBtnsContainer } from 'components/atoms/Layout'
import { PhoneInput } from 'components/molecules/PhoneInput'

interface Props {
  authentication: Authentication
  nextPage(page: string): void
  previousPage(page: string): void
  updateAccountMobilePhoneHelper(
    input: UpdateAccountMobilePhoneVariables
  ): Promise<UpdateAccountMobilePhoneResponseData>
  setSubmitErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
}

interface Values {
  mobilePhone: string
}

function getEmptyInitialValues(): Values {
  const initialValues: Values = {
    mobilePhone: ''
  }
  return initialValues
}

const validationSchema = Yup.object({
  mobilePhone: Yup.string()
    .matches(phoneRegExp, 'Mobile phone number is not valid')
    .required('Required')
})

const UserProfileChangeMobilePhoneForm = (props: Props): JSX.Element => {
  const {
    authentication,
    nextPage,
    previousPage,
    updateAccountMobilePhoneHelper,
    setSubmitErrorMessage
  } = props
  const userprofile = useSelector((state: RootState) => state.userprofile)
  const [loading, setLoading] = useState(true)
  const [formIsDirty, setFormIsDirty] = useState(false)
  const [initialVals, setInitialVals] = useState<Values>(() =>
    getEmptyInitialValues()
  )

  useEffect(() => {
    const initial = setInitialValues(authentication, userprofile)
    setInitialVals(initial)

    setLoading(false)
  }, [])

  function setInitialValues(
    authentication: Authentication,
    userprofile: UserProfile
  ): Values {
    const initialValues: Values = {
      mobilePhone:
        authentication.account && authentication.account.mobilePhone
          ? authentication.account.mobilePhone
          : ''
    }

    if (userprofile.isActive) {
      if (userprofile.mobilePhone) {
        initialValues.mobilePhone = userprofile.mobilePhone
      }

      setFormIsDirty(true)
    }

    return initialValues
  }

  function onSubmitWrapper(
    formik: FormikProps<Values>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()

    // only want to save data if something has changed
    if (formik.dirty || formIsDirty) {
      formik.handleSubmit(e)
    } else {
      setSubmitErrorMessage(
        'You have not changed the phone number so not update performed.'
      )
    }
  }

  async function onSubmit(
    values: Values,
    { setSubmitting }: FormikHelpers<Values>
  ): Promise<void> {
    const input = {
      updateMobilePhoneData: {
        mobilePhoneNumber: values.mobilePhone
      }
    }
    const updateAccountMobilePhoneResult = await updateAccountMobilePhoneHelper(
      input
    )
    if (updateAccountMobilePhoneResult?.success) {
      nextPage('changeMobilePhoneVerify')
    } else {
      setSubmitErrorMessage(
        updateAccountMobilePhoneResult?.errorMessage
          ? updateAccountMobilePhoneResult?.errorMessage
          : 'unknown error happended'
      )
      setSubmitting(false)
    }
  }

  const onCancel = () => {
    previousPage('edit')
  }

  if (loading) return <div>Loading ...</div>

  return (
    <Formik
      initialValues={initialVals}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(formik) => {
        return (
          <Form
            className='user-profile-change-mobile-phone-form'
            onSubmit={(e) => onSubmitWrapper(formik, e)}
          >
            <div id='newUserProfileChangeMobilePhoneForm'>
              <div className='row'>
                <div className='form-group col-md col-sm-12'>
                  <label htmlFor='mobilePhone'>New Mobile Phone</label>
                  <Field
                    type='text'
                    id='mobilePhone'
                    name='mobilePhone'
                    as={PhoneInput}
                    data-testid='user-profile-changed-mobile-phone-field'
                    placeholder=''
                    className={
                      formik.errors.mobilePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                  />
                  <ErrorMessage
                    name='mobilePhone'
                    render={(msg) => (
                      <div
                        className='text-danger'
                        data-testid='user-profile-changed-mobile-phone-field-error'
                      >
                        {msg}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className='notification-charges-info'>
              Standard call, message, and data rates may apply.
              <br />
              <br />
              By selecting Next you consent to get a text message confirming
              your contact information.
            </div>

            <ButtonsContainer className='p-0 btns-container'>
              <TwoBtnsContainer>
                <Button
                  variant='outline-secondary'
                  onClick={onCancel}
                  className='btn-cancel mt-0'
                >
                  Cancel
                </Button>
                <Button type='submit' className='btn btn-submit w-100'>
                  Next
                </Button>
              </TwoBtnsContainer>
            </ButtonsContainer>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UserProfileChangeMobilePhoneForm
